import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  Clock,
  Instagram,
  MessageCircle,
  Navigation,
  UtensilsCrossed,
  Heart,
  Lock,
} from "lucide-react";
import { businessInfo } from "../data/business";

export default function Footer() {
  return (
    <footer className="bg-stone-900 text-stone-300 border-t border-stone-800">
      {/* MAIN FOOTER */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12">
          
          {/* COLUMN 1: BRAND INFO (5 Cols) */}
          <div className="lg:col-span-5 space-y-4">
            <div className="flex items-center gap-2.5">
              <img
                src="/logo.png"
                alt="HimCream Naturals"
                className="h-10 w-auto brightness-110"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              <span className="text-xl font-extrabold text-white tracking-tight">
                HimCream <span className="text-sky-400">Naturals</span>
              </span>
            </div>

            <p className="text-sm text-stone-400 leading-relaxed pr-4">
              Udaipur's destination for artisanal natural ice cream crafted with 100% real seasonal fruits and pure farm milk. Zero artificial colors, zero synthetic flavors.
            </p>

            <div className="space-y-2 pt-2 text-xs sm:text-sm text-stone-300">
              <div className="flex items-center gap-2.5">
                <span className="text-sky-400 font-semibold">Owner:</span>
                <span className="text-white font-bold">{businessInfo.owner}</span>
              </div>
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-sky-400 shrink-0 mt-0.5" />
                <span>{businessInfo.address.full}</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-sky-400 shrink-0" />
                <a
                  href={`tel:${businessInfo.contact.phone}`}
                  className="hover:text-white transition font-medium"
                >
                  {businessInfo.contact.phoneDisplay}
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <Clock className="w-4 h-4 text-sky-400 shrink-0" />
                <span>{businessInfo.timings.hours} ({businessInfo.timings.days})</span>
              </div>
            </div>
          </div>

          {/* COLUMN 2: QUICK LINKS (3 Cols) */}
          <div className="lg:col-span-3 space-y-4">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">
              Explore
            </h3>
            <ul className="space-y-2 text-sm text-stone-400">
              <li>
                <Link to="/" className="hover:text-sky-400 transition">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-sky-400 transition">
                  About Our Philosophy
                </Link>
              </li>
              <li>
                <Link to="/menu" className="hover:text-sky-400 transition">
                  Menu Showcase
                </Link>
              </li>
              <li>
                <Link to="/gallery" className="hover:text-sky-400 transition">
                  Photo Gallery
                </Link>
              </li>
              <li>
                <Link to="/social" className="hover:text-sky-400 transition">
                  Instagram & Reels
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-sky-400 transition">
                  Visit & Contact Us
                </Link>
              </li>
              <li className="pt-2 border-t border-stone-800">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-300 transition"
                >
                  <Lock className="w-3 h-3" />
                  <span>Staff Portal</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* COLUMN 3: CONNECT & TABLE ORDERING (4 Cols) */}
          <div className="lg:col-span-4 space-y-4">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">
              Connect & Visit
            </h3>

            {/* Social Buttons */}
            <div className="flex flex-wrap gap-2 pt-1">
              <a
                href={businessInfo.contact.instagramUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-stone-800 hover:bg-stone-700 text-stone-200 text-xs font-medium rounded-lg transition"
              >
                <Instagram className="w-3.5 h-3.5 text-pink-400" />
                <span>@{businessInfo.contact.instagram}</span>
              </a>

              <a
                href={`https://wa.me/${businessInfo.contact.whatsapp.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-stone-800 hover:bg-stone-700 text-stone-200 text-xs font-medium rounded-lg transition"
              >
                <MessageCircle className="w-3.5 h-3.5 text-green-400" />
                <span>WhatsApp</span>
              </a>

              <a
                href={businessInfo.contact.mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-stone-800 hover:bg-stone-700 text-stone-200 text-xs font-medium rounded-lg transition"
              >
                <Navigation className="w-3.5 h-3.5 text-sky-400" />
                <span>Get Directions</span>
              </a>
            </div>

            {/* Dine-In Table Ordering Banner */}
            <div className="p-4 rounded-2xl bg-gradient-to-br from-stone-800 to-stone-800/60 border border-stone-700/60 space-y-2 mt-4">
              <div className="flex items-center gap-2 text-sky-400 font-bold text-sm">
                <UtensilsCrossed className="w-4 h-4" />
                <span>Dining At Our Café?</span>
              </div>
              <p className="text-xs text-stone-400">
                Sit down at any table in our Udaipur store and order straight from your mobile.
              </p>
              <Link
                to="/select-table"
                className="inline-flex items-center justify-center w-full gap-2 bg-sky-500 hover:bg-sky-600 text-white text-xs sm:text-sm font-bold py-2.5 px-4 rounded-xl shadow transition"
              >
                <span>Order at Your Table</span>
                <span>→</span>
              </Link>
            </div>
          </div>

        </div>
      </div>

      {/* BOTTOM COPYRIGHT & BRANDING BAR */}
      <div className="border-t border-stone-800 py-6 text-center text-xs text-stone-500">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-3 text-center md:text-left">
          <p>© {new Date().getFullYear()} HimCream Naturals. All rights reserved.</p>

          <p className="flex items-center justify-center flex-wrap gap-1.5 text-xs text-stone-400">
            <span>Website developed by</span>
            <a
              href="https://www.instagram.com/_gauranshjaroli_/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-400 hover:text-sky-300 font-semibold hover:underline underline-offset-2 transition"
            >
              Gauransh Jaroli
            </a>
            <span className="text-stone-600">|</span>
            <a
              href="https://www.parshwebcraft.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-stone-200 hover:text-white font-semibold hover:underline underline-offset-2 transition"
            >
              ParshWebCraft
            </a>
          </p>

          <p className="text-[11px] text-stone-500">
            Crafted with ❤️ in Udaipur
          </p>
        </div>
      </div>
    </footer>
  );
}
